import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AccessTime as ClockIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { GamifyToast } from '../../../common/CustomToasts';

const stripePaymentProcessor = /* GraphQL */ `
  mutation StripePaymentProcessor($request: AWSJSON!) {
    stripePaymentProcessor(request: $request)
  }
`;

const maintainEstimateOrder = /* GraphQL */ `
  mutation MaintainEstimateOrder($request: AWSJSON!) {
    maintainEstimateOrder(request: $request)
  }
`;

const contractorEstimateUpload = /* GraphQL */ `
  mutation ContractorEstimateUpload($request: AWSJSON!) {
    contractorEstimateUpload(request: $request)
  }
`;

interface EstimateOrder {
  id: string;
  dealID: string;
  orgID: string;
  userID: string;
  dealName?: string;
  dealAddress?: string;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  amount: number;
  currency: string;
  stripeCustomerID?: string;
  stripeUsageRecordID?: string;
  pdfUrl?: string;
  orderedAt: string;
  readyAt?: string;
}

interface DealEstimatesProps {
  dealID: string;
  dealInfo?: any;
}

const ESTIMATE_PRICE = 99.99;
const PROCESSING_DELAY_MS = 7000;

const DealEstimates: React.FC<DealEstimatesProps> = ({ dealID, dealInfo }) => {
  const { theme } = useTheme();
  const [orders, setOrders] = useState<EstimateOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // const [isOrdering, setIsOrdering] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [uploadingOrderId, setUploadingOrderId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadingOrderIdRef = useRef<string | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!dealID) {
      setError('No deal ID provided');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request = JSON.stringify({
        operation: 'getEstimateOrdersByDeal',
        dealID,
      });

      const result: any = await API.graphql(
        graphqlOperation(maintainEstimateOrder, { request })
      );

      const responseData = result?.data?.maintainEstimateOrder;
      if (responseData) {
        let parsedResponse = responseData;
        while (typeof parsedResponse === 'string') {
          try {
            parsedResponse = JSON.parse(parsedResponse);
          } catch {
            break;
          }
        }

        if (parsedResponse.success && parsedResponse.data?.estimateOrders) {
          setOrders(parsedResponse.data.estimateOrders);
        } else if (parsedResponse.success) {
          setOrders([]);
        } else {
          throw new Error(
            parsedResponse.error || 'Failed to fetch estimate orders'
          );
        }
      } else {
        setOrders([]);
      }
    } catch (err: any) {
      console.error('Error fetching estimate orders:', err);
      setError(err.message || 'Failed to load estimate orders');
    } finally {
      setIsLoading(false);
    }
  }, [dealID]);

  useEffect(() => {
    fetchOrders();
    return () => {
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
      }
    };
  }, [fetchOrders]);

  // const handleOrderEstimate = () => {
  //   setShowConfirmModal(true);
  // };

  const handleConfirmOrder = async () => {
    setShowConfirmModal(false);
    // setIsOrdering(true);

    try {
      const request = JSON.stringify({
        operation: 'processEstimatePayment',
        dealID,
        dealName: dealInfo?.name || dealInfo?.customerName || '',
        dealAddress:
          dealInfo?.address ||
          dealInfo?.streetAddress ||
          dealInfo?.fullAddress ||
          '',
      });

      const result: any = await API.graphql(
        graphqlOperation(stripePaymentProcessor, { request })
      );

      const responseData = result?.data?.stripePaymentProcessor;
      if (responseData) {
        let parsedResponse = responseData;
        while (typeof parsedResponse === 'string') {
          try {
            parsedResponse = JSON.parse(parsedResponse);
          } catch {
            break;
          }
        }

        if (parsedResponse.success && parsedResponse.data?.estimateOrder) {
          const newOrder = parsedResponse.data.estimateOrder;
          setOrders((prev) => [newOrder, ...prev]);

          GamifyToast.success(
            'Estimate Ordered! Your Xactimate estimate is being processed...'
          );

          if (newOrder.status === 'PROCESSING') {
            processingTimerRef.current = setTimeout(async () => {
              await markOrderReady(newOrder.id);
            }, PROCESSING_DELAY_MS);
          }
        } else {
          throw new Error(
            parsedResponse.error || 'Failed to process estimate order'
          );
        }
      }
    } catch (err: any) {
      console.error('Error ordering estimate:', err);
      GamifyToast.error(
        err.message || 'Failed to order estimate. Please try again.'
      );
    } finally {
      // setIsOrdering(false);
    }
  };

  const markOrderReady = async (orderId: string, pdfUrl?: string) => {
    try {
      const request = JSON.stringify({
        operation: 'markOrderReady',
        orderID: orderId,
        ...(pdfUrl && { pdfUrl }),
      });
      console.log('[EstimateUpload] markOrderReady request:', request);

      const result: any = await API.graphql(
        graphqlOperation(stripePaymentProcessor, { request })
      );

      const responseData = result?.data?.stripePaymentProcessor;
      if (responseData) {
        let parsedResponse = responseData;
        while (typeof parsedResponse === 'string') {
          try {
            parsedResponse = JSON.parse(parsedResponse);
          } catch {
            break;
          }
        }

        if (parsedResponse.success && parsedResponse.data?.estimateOrder) {
          const updatedOrder = parsedResponse.data.estimateOrder;
          setOrders((prev) =>
            prev.map((o) => (o.id === orderId ? updatedOrder : o))
          );
          GamifyToast.success(
            'Estimate Ready! Your Xactimate estimate is now available to view.'
          );
        }
      }
    } catch (err: any) {
      console.error('Error marking order as ready:', err);
    }
  };

  const handleUploadEstimate = (order: EstimateOrder) => {
    setUploadingOrderId(order.id);
    uploadingOrderIdRef.current = order.id;
    // Trigger the hidden file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();

      // Detect cancel: when the window regains focus after the file picker closes
      // without a file being selected, reset the state
      const handleFocusBack = () => {
        setTimeout(() => {
          if (fileInputRef.current && !fileInputRef.current.files?.length) {
            setUploadingOrderId(null);
            uploadingOrderIdRef.current = null;
            setUploadProgress(0);
          }
        }, 300);
        window.removeEventListener('focus', handleFocusBack);
      };
      window.addEventListener('focus', handleFocusBack);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      // User cancelled the file picker
      setUploadingOrderId(null);
      uploadingOrderIdRef.current = null;
      setUploadProgress(0);
      return;
    }

    // Validate file type
    if (file.type !== 'application/pdf') {
      GamifyToast.error('Please select a PDF file.');
      setUploadingOrderId(null);
      uploadingOrderIdRef.current = null;
      return;
    }

    // Validate file size (50MB max)
    const MAX_SIZE = 50 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      GamifyToast.error('File is too large. Maximum size is 50 MB.');
      setUploadingOrderId(null);
      uploadingOrderIdRef.current = null;
      return;
    }

    // Extract orgID from the dealID prop (format: "orgID:uuid")
    const orgID = dealID.includes(':')
      ? dealID.split(':')[0]
      : (global as any)?.me?.orgID;
    if (!orgID) {
      GamifyToast.error('Unable to determine organization. Please try again.');
      setUploadingOrderId(null);
      uploadingOrderIdRef.current = null;
      return;
    }

    setUploadProgress(10);

    try {
      // Step 1: Get presigned URL from our Lambda
      const presignedRequest = JSON.stringify({
        operation: 'getPresignedUrl',
        dealID,
        orgID,
      });

      const presignedResult: any = await API.graphql(
        graphqlOperation(contractorEstimateUpload, {
          request: presignedRequest,
        })
      );

      let presignedResponse = presignedResult?.data?.contractorEstimateUpload;
      while (typeof presignedResponse === 'string') {
        try {
          presignedResponse = JSON.parse(presignedResponse);
        } catch {
          break;
        }
      }

      if (!presignedResponse?.success) {
        throw new Error(presignedResponse?.error || 'Failed to get upload URL');
      }

      const { presignedUrl, s3Key } = presignedResponse.data;
      setUploadProgress(30);

      // Step 2: Upload the file directly to S3 via presigned URL
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/pdf',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      setUploadProgress(70);

      // Step 3: Confirm the upload (creates/updates DealFile record)
      const confirmRequest = JSON.stringify({
        operation: 'confirmUpload',
        dealID,
        orgID,
        s3Key,
      });

      const confirmResult: any = await API.graphql(
        graphqlOperation(contractorEstimateUpload, { request: confirmRequest })
      );

      let confirmResponse = confirmResult?.data?.contractorEstimateUpload;
      while (typeof confirmResponse === 'string') {
        try {
          confirmResponse = JSON.parse(confirmResponse);
        } catch {
          break;
        }
      }

      if (!confirmResponse?.success) {
        throw new Error(confirmResponse?.error || 'Failed to confirm upload');
      }

      setUploadProgress(90);

      // Step 4: Mark the estimate order as ready with the actual PDF URL
      const uploadedUrl = confirmResponse.data?.url;
      console.log(
        '[EstimateUpload] confirmUpload response:',
        JSON.stringify(confirmResponse)
      );
      console.log(
        '[EstimateUpload] PDF URL being passed to markOrderReady:',
        uploadedUrl
      );
      await markOrderReady(uploadingOrderIdRef.current!, uploadedUrl);

      setUploadProgress(100);
      GamifyToast.success('Estimate uploaded successfully!');

      // Refresh the orders list
      await fetchOrders();
    } catch (err: any) {
      console.error('Error uploading estimate:', err);
      GamifyToast.error(
        err.message || 'Failed to upload estimate. Please try again.'
      );
    } finally {
      setUploadingOrderId(null);
      uploadingOrderIdRef.current = null;
      setUploadProgress(0);
    }
  };

  const handleViewOrder = async (order: EstimateOrder) => {
    if (order.status === 'READY' && order.pdfUrl) {
      try {
        // Use Storage.get to generate a presigned URL (same pattern as measurement report viewer)
        // The pdfUrl stores the relative S3 key (e.g., 'xactimate/org/deal/estimate.pdf')
        const signedUrl = await Storage.get(order.pdfUrl, { level: 'public' });
        setPdfUrl(signedUrl as string);
        setShowPDFViewer(true);
      } catch (err) {
        console.error('[EstimateUpload] Error generating signed URL:', err);
        GamifyToast.error('Failed to load estimate. Please try again.');
      }
    } else if (order.status === 'PROCESSING') {
      GamifyToast.info(
        'Your estimate is being prepared. Please check back shortly.'
      );
    }
  };

  const handleDownloadEstimate = async (order: EstimateOrder) => {
    if (!order.pdfUrl) return;
    try {
      const signedUrl = await Storage.get(order.pdfUrl, { level: 'public' });
      const response = await fetch(signedUrl as string);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `xactimate_estimate_${order.dealID || 'report'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('[EstimateUpload] Download failed:', err);
      GamifyToast.error('Failed to download estimate. Please try again.');
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'READY':
        return '#4CAF50';
      case 'PROCESSING':
        return '#FF9800';
      case 'FAILED':
        return '#F44336';
      case 'PENDING':
      default:
        return theme.textLight;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'READY':
        return 'Ready to View';
      case 'PROCESSING':
        return 'Processing...';
      case 'FAILED':
        return 'Failed';
      case 'PENDING':
        return 'Pending';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'READY':
        return <CheckIcon sx={{ fontSize: 14 }} />;
      case 'PROCESSING':
        return <ClockIcon sx={{ fontSize: 14 }} />;
      case 'FAILED':
        return <CloseIcon sx={{ fontSize: 14 }} />;
      default:
        return <ClockIcon sx={{ fontSize: 14 }} />;
    }
  };

  const renderHeader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.text }}>
        Estimates
      </Typography>
    </Box>
  );

  const renderLoading = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
      }}
    >
      <CircularProgress size={32} sx={{ color: theme.primary }} />
      <Typography sx={{ mt: 2, color: theme.textLight, textAlign: 'center' }}>
        Loading estimates...
      </Typography>
    </Box>
  );

  const renderError = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
      }}
    >
      <CloseIcon sx={{ fontSize: 40, color: '#FF0000', mb: 2 }} />
      <Typography sx={{ color: theme.text, textAlign: 'center' }}>
        Error loading estimates
      </Typography>
      <Typography
        sx={{
          mt: 1,
          color: theme.textLight,
          textAlign: 'center',
          fontSize: 14,
        }}
      >
        {error}
      </Typography>
      <Button
        onClick={fetchOrders}
        variant="contained"
        sx={{
          mt: 2,
          backgroundColor: theme.primary,
          '&:hover': { backgroundColor: theme.primaryDark },
        }}
      >
        Retry
      </Button>
    </Box>
  );

  const renderEmpty = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
      }}
    >
      <FolderIcon sx={{ fontSize: 40, color: theme.textLight, mb: 2 }} />
      <Typography sx={{ color: theme.textLight, textAlign: 'center' }}>
        No estimates yet
      </Typography>
    </Box>
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    GamifyToast.success('Estimate ID copied to clipboard');
  };

  const renderOrderItem = (order: EstimateOrder, index: number) => (
    <Box
      key={order.id || index}
      onClick={() => handleViewOrder(order)}
      sx={{
        backgroundColor: theme.background2,
        borderRadius: 2,
        p: 2,
        mb: 1.5,
        borderLeft: `4px solid ${getStatusColor(order.status)}`,
        cursor: order.status === 'READY' ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        opacity: order.status === 'READY' ? 1 : 0.9,
        '&:hover':
          order.status === 'READY'
            ? {
                backgroundColor: theme.background3,
                transform: 'translateX(4px)',
              }
            : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{ fontWeight: 600, color: theme.text, mb: 0.5, fontSize: 16 }}
          >
            Xactimate Estimate
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.textLight }}>
            Ordered {formatDate(order.orderedAt)}
          </Typography>
        </Box>
        <Chip
          icon={
            order.status === 'PROCESSING' ? (
              <CircularProgress
                size={12}
                sx={{ color: getStatusColor(order.status) }}
              />
            ) : (
              getStatusIcon(order.status)
            )
          }
          label={getStatusLabel(order.status)}
          size="small"
          sx={{
            backgroundColor: `${getStatusColor(order.status)}20`,
            color: getStatusColor(order.status),
            fontWeight: 600,
            fontSize: 12,
            '& .MuiChip-icon': {
              color: getStatusColor(order.status),
            },
          }}
        />
      </Box>

      {/* Estimate ID Section */}
      <Box
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: `1px solid ${theme.stroke}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{ fontSize: 11, color: theme.textLight, fontWeight: 500 }}
            >
              ESTIMATE ID
            </Typography>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(order.id);
              }}
              sx={{
                backgroundColor: theme.background3,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                cursor: 'pointer',
                border: `1px solid ${theme.stroke}`,
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: theme.background1,
                  borderColor: theme.primary,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: theme.text,
                  letterSpacing: '0.5px',
                }}
              >
                {order.id}
              </Typography>
            </Box>
          </Box>
          {order.status === 'READY' && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.primary,
                  mr: 0.5,
                }}
              >
                View
              </Typography>
              <ChevronRightIcon sx={{ color: theme.primary, fontSize: 20 }} />
            </Box>
          )}
        </Box>
      </Box>

      {order.status === 'PROCESSING' && (
        <Box sx={{ mt: 1.5 }}>
          {uploadingOrderId === order.id ? (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: `${theme.primary}20`,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.primary,
                    borderRadius: 3,
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  color: theme.textLight,
                  mt: 0.5,
                  textAlign: 'center',
                }}
              >
                Uploading estimate...
              </Typography>
            </Box>
          ) : (
            <>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadEstimate(order);
                }}
                variant="contained"
                startIcon={<UploadIcon />}
                size="small"
                sx={{
                  backgroundColor: theme.primary,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  '&:hover': { backgroundColor: theme.primaryDark },
                }}
              >
                Upload Estimate PDF
              </Button>
              <Typography
                sx={{
                  fontSize: 11,
                  color: theme.textLight,
                  mt: 1,
                  fontStyle: 'italic',
                }}
              >
                Your estimate is being prepared. Upload the completed PDF when
                ready.
              </Typography>
            </>
          )}
        </Box>
      )}

      {order.status === 'READY' && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1,
            borderTop: `1px solid ${theme.stroke || '#E0E0E0'}`,
          }}
        >
          {uploadingOrderId === order.id ? (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: `${theme.primary}20`,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.primary,
                    borderRadius: 3,
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  color: theme.textLight,
                  mt: 0.5,
                  textAlign: 'center',
                }}
              >
                Uploading updated estimate...
              </Typography>
            </Box>
          ) : (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleUploadEstimate(order);
              }}
              variant="outlined"
              startIcon={<UploadIcon />}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 12,
                borderColor: theme.primary,
                color: theme.primary,
                '&:hover': {
                  backgroundColor: `${theme.primary}10`,
                  borderColor: theme.primary,
                },
              }}
            >
              Update Estimate
            </Button>
          )}
          {!uploadingOrderId && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadEstimate(order);
              }}
              variant="outlined"
              startIcon={<DownloadIcon />}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 12,
                ml: 1,
                borderColor: theme.textLight,
                color: theme.textLight,
                '&:hover': {
                  backgroundColor: `${theme.textLight}10`,
                  borderColor: theme.text,
                  color: theme.text,
                },
              }}
            >
              Download
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  const renderOrdersList = () => (
    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
      {orders.map((order, index) => renderOrderItem(order, index))}
    </Box>
  );

  const renderConfirmModal = () => (
    <Dialog
      open={showConfirmModal}
      onClose={() => setShowConfirmModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.background1,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: theme.text, textAlign: 'center', pt: 4 }}>
        Order Xactimate Estimate
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontSize: 36,
            fontWeight: 700,
            color: theme.primary,
            my: 2,
          }}
        >
          {formatCurrency(ESTIMATE_PRICE)}
        </Typography>
        <Typography sx={{ color: theme.textLight, mb: 3 }}>
          A professional Xactimate insurance claim estimate will be prepared for
          this property. Orders are typically processed within a few hours.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1.5 }}>
        <Button
          onClick={() => setShowConfirmModal(false)}
          sx={{
            flex: 1,
            py: 1.5,
            backgroundColor: theme.background2,
            color: theme.text,
            '&:hover': { backgroundColor: theme.background3 },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmOrder}
          variant="contained"
          sx={{
            flex: 1,
            py: 1.5,
            backgroundColor: theme.primary,
            '&:hover': { backgroundColor: theme.primaryDark },
          }}
        >
          Confirm Purchase
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPDFViewer = () => (
    <Dialog
      open={showPDFViewer}
      onClose={() => setShowPDFViewer(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogContent
        sx={{ padding: 0, overflow: 'hidden', position: 'relative' }}
      >
        <IconButton
          onClick={() => setShowPDFViewer(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            zIndex: 1000,
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '85vh' }}
            title="Xactimate Estimate"
          />
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ p: 2 }}>
      {renderHeader()}
      {isLoading && renderLoading()}
      {error && !isLoading && renderError()}
      {!isLoading && !error && orders.length === 0 && renderEmpty()}
      {!isLoading && !error && orders.length > 0 && renderOrdersList()}
      {renderConfirmModal()}
      {renderPDFViewer()}
      {/* Hidden file input for PDF upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
    </Box>
  );
};

export default DealEstimates;
